/**
 * WhishInstructionsModal Component
 *
 * Manual Whish Money payment popup (used until the Whish Pay API is live).
 * Shows the two ways a guest can pay the DARAK admin account - an in-app
 * Whish-to-Whish transfer or a cash payment at a Whish agent - then lets
 * the guest flag the payment as sent for admin verification.
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';

// DARAK admin Whish receiving account.
const WHISH_NAME = 'Joseph Hamawi';
const WHISH_NUMBER = '+961 70 000 406';
const WHISH_NUMBER_RAW = '+96170000406';

type Method = 'whish' | 'cash';

interface WhishInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingCode: string;
  amount: string;
  isSubmitting: boolean;
  onConfirmSent: () => void;
}

export const WhishInstructionsModal: React.FC<WhishInstructionsModalProps> = ({
  isOpen,
  onClose,
  bookingCode,
  amount,
  isSubmitting,
  onConfirmSent,
}) => {
  const [method, setMethod] = useState<Method>('whish');

  if (!isOpen) return null;

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      // Clipboard blocked - no-op; the value is visible to copy manually.
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Pay with Whish</h3>
            <p className="text-sm text-gray-500">Send {amount} to secure your booking</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Method toggle */}
          <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-xl">
            <button
              onClick={() => setMethod('whish')}
              className={`py-2 text-sm font-medium rounded-lg transition-colors ${
                method === 'whish' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
              }`}
            >
              Whish to Whish
            </button>
            <button
              onClick={() => setMethod('cash')}
              className={`py-2 text-sm font-medium rounded-lg transition-colors ${
                method === 'cash' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
              }`}
            >
              Cash at agent
            </button>
          </div>

          {/* Recipient card (shared by both methods) */}
          <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
            <button
              onClick={() => copy(WHISH_NUMBER_RAW, 'Number')}
              className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50"
            >
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Whish number</p>
                <p className="font-mono text-base font-semibold text-gray-900">{WHISH_NUMBER}</p>
              </div>
              <span className="text-xs font-medium text-primary-600">Copy</span>
            </button>
            <div className="p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Recipient name</p>
              <p className="text-base font-semibold text-gray-900">{WHISH_NAME}</p>
            </div>
            <button
              onClick={() => copy(bookingCode, 'Booking code')}
              className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50"
            >
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Booking code (put in note)
                </p>
                <p className="font-mono text-sm text-gray-900">{bookingCode}</p>
              </div>
              <span className="text-xs font-medium text-primary-600">Copy</span>
            </button>
          </div>

          {/* Steps */}
          {method === 'whish' ? (
            <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-600">
              <li>Open the Whish Money app</li>
              <li>Tap <span className="font-medium text-gray-900">Send Money</span></li>
              <li>Enter the number <span className="font-mono">{WHISH_NUMBER}</span> ({WHISH_NAME})</li>
              <li>Enter the amount <span className="font-medium text-gray-900">{amount}</span></li>
              <li>Add your booking code in the note</li>
              <li>Confirm the transfer</li>
            </ol>
          ) : (
            <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-600">
              <li>Go to any Whish Money agent or shop</li>
              <li>
                Ask to send cash to <span className="font-mono">{WHISH_NUMBER}</span> ({WHISH_NAME})
              </li>
              <li>Pay the agent <span className="font-medium text-gray-900">{amount}</span> in cash</li>
              <li>Give the agent your booking code for the note</li>
              <li>Keep the agent receipt</li>
            </ol>
          )}

          <p className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
            Once we receive your payment, our team confirms it and your booking is
            secured. Please pay within 24 hours. After sending, tap the button below
            so we know to look for it.
          </p>

          {/* Confirm */}
          <button
            onClick={onConfirmSent}
            disabled={isSubmitting}
            className="w-full py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Notifying team...' : "I've sent the payment"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhishInstructionsModal;
